// src/pages/Listings.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getListings } from '../services/ListingsService';
import './Listings.css';

import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Use local placeholder to avoid external network failures
const placeholderImage = '/placeholder.jpg';

function AutoFitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers && markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(marker => [marker.lat, marker.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers, map]);
  return null;
}

function Listings() {
  const [listings, setListings] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    const fetchAndGeocodeListings = async () => {
      try {
        // --- CHANGE: Fetch initial data and display it immediately ---
        const initialListings = await getListings();
        setListings(initialListings); // Render the page right away!

        const cachedCoords = JSON.parse(localStorage.getItem('geoCoordsCache')) || {};
        let cacheDirty = false;
        const listingsToProcess = [];
        const normalizeLocation = location => 
          typeof location === 'string' ? location.trim() : '';
        
        // Populate the initial state with any listings that are already cached
        const listingsWithCachedCoords = initialListings.map(listing => {
          const normalizedLocation = normalizeLocation(listing.location);
          const cachedEntry = cachedCoords[listing.id];
          const docCoords = listing.coordinates;
          const hasDocCoords =
            docCoords &&
            typeof docCoords.lat === 'number' &&
            typeof docCoords.lng === 'number';

          if (!normalizedLocation) {
            if (cachedEntry) {
              delete cachedCoords[listing.id];
              cacheDirty = true;
            }
            return { ...listing, lat: undefined, lng: undefined };
          }

          if (hasDocCoords) {
            const coords = { lat: docCoords.lat, lng: docCoords.lng };
            if (
              !cachedEntry ||
              cachedEntry.location !== normalizedLocation ||
              cachedEntry.lat !== coords.lat ||
              cachedEntry.lng !== coords.lng
            ) {
              cachedCoords[listing.id] = { ...coords, location: normalizedLocation };
              cacheDirty = true;
            }
            return { ...listing, ...coords };
          }

          if (cachedEntry && cachedEntry.location === normalizedLocation) {
            return { ...listing, lat: cachedEntry.lat, lng: cachedEntry.lng };
          }

          if (cachedEntry && cachedEntry.location !== normalizedLocation) {
            delete cachedCoords[listing.id];
            cacheDirty = true;
          }

          listingsToProcess.push({ listing, normalizedLocation }); // Add to the queue for geocoding
          return listing;
        });
        setListings(listingsWithCachedCoords);


        // If everything was in the cache, we're done.
        if (listingsToProcess.length === 0) {
          if (cacheDirty) {
            localStorage.setItem('geoCoordsCache', JSON.stringify(cachedCoords));
          }
          return;
        }

        // --- CHANGE: Process the remaining listings in the background ---
        for (const { listing, normalizedLocation } of listingsToProcess) {
          if (normalizedLocation) {
            try {
              const query = encodeURIComponent(normalizedLocation);
              const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`);
              const data = await response.json();
              if (data && data.length > 0) {
                const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
                cachedCoords[listing.id] = { ...coords, location: normalizedLocation }; // Update cache object
                cacheDirty = true;
                
                // --- CHANGE: Update state inside the loop for a "live" feel ---
                setListings(prevListings => 
                  prevListings.map(l => l.id === listing.id ? { ...l, ...coords, coordinates: coords } : l)
                );
              }
            } catch (geoError) {
              console.error("Geocoding error:", geoError);
            }
            
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        // Save the fully populated cache at the very end
        if (cacheDirty) {
          localStorage.setItem('geoCoordsCache', JSON.stringify(cachedCoords));
        }

      } catch (err) {
        console.error("Error fetching listings:", err);
        setError("Failed to load listings. Please try again later.");
      }
    };

    fetchAndGeocodeListings();
  }, []);

  const filteredListings = useMemo(() => {
    return listings.filter(listing => {
        const matchesSearch = searchTerm === '' ||
          listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          listing.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === '' || listing.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });
  }, [listings, searchTerm, categoryFilter]);

  const listingsWithLocations = useMemo(() => {
    return filteredListings.filter(
      listing => Number.isFinite(listing.lat) && Number.isFinite(listing.lng)
    );
  }, [filteredListings]);

  if (error) {
    return <div className="listings-container">{error}</div>;
  }

  return (
    <div className="listings-container">
      <div className="listings-header">
        <h1>Available Listings</h1>
        <p>Discover amazing opportunities for learning and growth.</p>
      </div>

      <div className="map-container">
        <MapContainer 
          center={[42.9849, -81.2453]}
          zoom={9} 
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {listingsWithLocations.map(listing => {
            const imageUrl = listing.media?.[0]?.url || listing.imageUrl || placeholderImage;
            const safeImageUrl = imageUrl ? imageUrl.replace(/"/g, '&quot;') : placeholderImage;
            const safeTitle = (listing.title || 'Listing image').replace(/"/g, '&quot;');
            const customIcon = L.divIcon({
              html: `\n                <div class="custom-marker-wrapper">\n                  <img src="${safeImageUrl}" alt="${safeTitle}" loading="lazy" />\n                </div>\n              `.trim(),
              className: 'custom-marker-icon',
              iconSize: [56, 56],
              iconAnchor: [28, 56],
              popupAnchor: [0, -52]
            });

            return (
              <Marker 
                key={listing.id} 
                position={[listing.lat, listing.lng]}
                icon={customIcon}
              >
                <Popup>
                  <strong>{listing.title}</strong><br />
                  {listing.location}<br />
                  <Link to={`/listings/${listing.id}`}>View Details</Link>
                </Popup>
              </Marker>
            );
          })}
          
          <AutoFitBounds markers={listingsWithLocations} />
        </MapContainer>
      </div>

      <div className="listings-filters">
        <input
          type="text"
          placeholder="Search listings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select 
          value={categoryFilter} 
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Categories</option>
          <option value="education">Education</option>
          <option value="recreation">Recreation</option>
          <option value="childcare">Childcare</option>
          <option value="mentorship">Mentorship</option>
          <option value="professional_development">Professional Development</option>
          <option value="other">Other</option>
        </select>
      </div>

      {filteredListings.length === 0 && (
          <div className="empty-listings">
              <p>No listings found. Try adjusting your filters.</p>
          </div>
      )}

      <div className="listings-grid">
        {filteredListings.map(listing => (
          <div key={listing.id} className="listing-card">
            <div className="listing-card-image" style={{ position: 'relative' }}>
              {(() => {
                const firstMedia = listing.media?.[0];
                const imageUrl = firstMedia?.url || listing.imageUrl;
                const isVideo = firstMedia?.type === 'video';
                
                if (imageUrl) {
                  if (isVideo) {
                    return (
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <video 
                          src={imageUrl} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          muted
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          pointerEvents: 'none'
                        }}>
                          ▶
                        </div>
                      </div>
                    );
                  } else {
                    return <img src={imageUrl} alt={listing.title} />;
                  }
                } else {
                  return <img src={placeholderImage} alt="No content available" />;
                }
              })()}
              
              {listing.media && listing.media.length > 1 && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '0.8rem'
                }}>
                  {listing.media.length} items
                </div>
              )}
            </div>
            
            <div className="listing-card-content">
              <h3>{listing.title}</h3>
              
              <div className="listing-category">
                {listing.category.charAt(0).toUpperCase() + 
                 listing.category.slice(1).replace('_', ' ')}
              </div>
              {listing.eventStart && (
                <div className="listing-date" style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                  {new Date(listing.eventStart).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                  {listing.eventEnd && (
                    <>
                      {' '}–{' '}
                      {new Date(listing.eventEnd).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                    </>
                  )}
                </div>
              )}
              
              <div className="listing-price">
                {listing.isFreeEvent ? 'Free Event' : 
                 listing.isContactForPrice ? 'Contact for Price' :
                 parseFloat(listing.price) > 0 ? `$${parseFloat(listing.price).toFixed(2)}` : ''}
              </div>
              
              <p className="listing-preview">
                {listing.description && listing.description.length > 100
                  ? listing.description.substring(0, 100) + '...'
                  : listing.description}
              </p>
              
              <Link to={`/listings/${listing.id}`} className="view-details-btn">
                View Details
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Listings;
